/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {
    ImportDuplicateRow,
    XlsxImportResult,
} from 'types/import/XlsxImportResult'

const toDuplicateRows = (value: unknown): ImportDuplicateRow[] => {
    if (!Array.isArray(value)) {
        return []
    }

    return value
        .map((item) => {
            if (!item || typeof item !== 'object') {
                return null
            }

            const row = item as Record<string, unknown>
            return {
                rowIndex:
                    typeof row.row_index === 'number'
                        ? row.row_index
                        : typeof row.rowIndex === 'number'
                          ? row.rowIndex
                          : undefined,
                identifier:
                    typeof row.identifier === 'string'
                        ? row.identifier
                        : typeof row.name === 'string'
                          ? row.name
                          : undefined,
                reason:
                    typeof row.reason === 'string'
                        ? row.reason
                        : typeof row.duplicate_reason === 'string'
                          ? row.duplicate_reason
                          : undefined,
                existingId:
                    typeof row.existing_id === 'number'
                        ? row.existing_id
                        : typeof row.existingId === 'number'
                          ? row.existingId
                          : undefined,
                incoming:
                    row.incoming && typeof row.incoming === 'object'
                        ? (row.incoming as Record<string, unknown>)
                        : undefined,
            } satisfies ImportDuplicateRow
        })
        .filter(Boolean) as ImportDuplicateRow[]
}

export const parseImportResponse = (
    response: unknown,
    fallbackImportedCount?: number
): XlsxImportResult => {
    if (!response || typeof response !== 'object') {
        return {
            success: false,
            importedCount: fallbackImportedCount ?? 0,
        }
    }

    const payload = response as Record<string, unknown>
    const duplicateRows = payload.duplicates ?? payload.duplicate_rows ?? payload.duplicateRows
    const existingLeadRows = payload.existing_leads ?? payload.existingLeads ?? payload.old_leads ?? payload.oldLeads
    const duplicates = toDuplicateRows(duplicateRows)
    const existingLeads = toDuplicateRows(existingLeadRows)

    const importedCountRaw =
        payload.imported_count ??
        payload.importedCount ??
        payload.created_count ??
        payload.createdCount

    const importedCount =
        typeof importedCountRaw === 'number'
            ? importedCountRaw
            : fallbackImportedCount

    const skippedCountRaw = payload.skipped_count ?? payload.skippedCount
    const duplicatesCountRaw =
        payload.duplicates_count ?? payload.duplicatesCount
    const existingLeadsCountRaw =
        payload.existing_leads_count ?? payload.existingLeadsCount
    const leadsCreatedCountRaw =
        payload.leads_created_count ?? payload.leadsCreatedCount

    const errors = Array.isArray(payload.errors)
        ? payload.errors.filter((item) => typeof item === 'string')
        : undefined

    return {
        success:
            typeof payload.success === 'boolean'
                ? payload.success
                : errors?.length
                  ? false
                  : true,
        importedCount,
        skippedCount:
            typeof skippedCountRaw === 'number' ? skippedCountRaw : undefined,
        duplicatesCount:
            typeof duplicatesCountRaw === 'number'
                ? duplicatesCountRaw
            : Array.isArray(duplicateRows)
              ? duplicates.length
              : undefined,
        duplicates: Array.isArray(duplicateRows) ? duplicates : undefined,
        existingLeadsCount:
            typeof existingLeadsCountRaw === 'number'
                ? existingLeadsCountRaw
                : Array.isArray(existingLeadRows)
                  ? existingLeads.length
                  : undefined,
        existingLeads: Array.isArray(existingLeadRows) ? existingLeads : undefined,
        leadsCreatedCount:
            typeof leadsCreatedCountRaw === 'number'
                ? leadsCreatedCountRaw
                : undefined,
        errors,
        message: typeof payload.message === 'string' ? payload.message : undefined,
    }
}
